'use client';

import './commitment-lists.scss';

import { useQueryState } from 'nuqs';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import { CommitmentTypeEnum } from '@sola/schema/dotcom/cart/commitments.js';
import { PaymentFrequencyType } from '@sola/schema/dotcom/proxy-api/transactions.js';
import {
  GraphQLCommitment,
  GraphQLGivingTotals,
  GraphQLPledge,
} from '@sola/schema/dotcom/supporters/schema.generated.js';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks.js';
import {
  commitmentsQuery,
  givingTotalsQuery,
  pledgesQuery,
} from '@sola/supporter-sdk/queries.js';

import { CommitmentCard } from '../../molecules/commitment-card/commitment-card.js';
import { CommitmentCardOverrideData } from '../../utils/commitments.js';
import { CTA_CONTACT_SUPPORT } from '../../utils/ctas.js';
import { formatPledgeFooter, formatPledgeName } from '../../utils/pledges.js';
import { SectionCardList } from '../section-card-list/section-card-list.js';
import {
  CommitmentCardData,
  getCommitmentTotal,
  getSortCommitmentsByTitle,
  groupCommitmentsByPaymentFrequency,
  groupCommitmentsByType,
  groupHasCommitments,
} from './utils.js';

const getCardElId = (id: string) => `commitment-card-${id}`;

/**
 * Get component data
 */
export const useComponentData = () => {
  const { t } = useTranslation('my');
  const { data, isLoading, isFetching, isPending, isError } = useSupporterQuery(
    [givingTotalsQuery, commitmentsQuery, pledgesQuery],
  );
  let commitmentFrequencyGroups: CommitmentCardData[] = [];

  // @ts-expect-error //TODO This should be fixed once we tighten up useSupporterQuery types.
  const [givingTotals, commitments, pledges]: [
    GraphQLGivingTotals | undefined,
    GraphQLCommitment[] | undefined,
    GraphQLPledge[] | undefined,
  ] = data;

  if (commitments?.length) {
    const {
      [PaymentFrequencyType.Monthly]: monthlyCommitments,
      [PaymentFrequencyType.Quarterly]: quarterlyCommitments,
      [PaymentFrequencyType.Semiannually]: semiannualCommitments,
      [PaymentFrequencyType.Annually]: annualCommitments,
    } = groupCommitmentsByPaymentFrequency(commitments);

    commitmentFrequencyGroups = [
      {
        heading: t('commitments.frequencies.monthly'),
        frequencyAbbr: t('commitments.frequencyAbbr.monthly'),
        commitments: monthlyCommitments,
      },
      {
        heading: t('commitments.frequencies.quarterly'),
        frequencyAbbr: t('commitments.frequencyAbbr.quarterly'),
        commitments: quarterlyCommitments,
      },
      {
        heading: t('commitments.frequencies.semiannual'),
        frequencyAbbr: t('commitments.frequencyAbbr.semiAnnual'),
        commitments: semiannualCommitments,
      },
      {
        heading: t('commitments.frequencies.annual'),
        frequencyAbbr: t('commitments.frequencyAbbr.annual'),
        commitments: annualCommitments,
      },
    ];
  }

  return {
    isShowingSkeletons: (isLoading || isFetching || isPending) && !isError,
    commitmentFrequencyGroups,
    givingTotals,
    sortCommitmentsByTitle: getSortCommitmentsByTitle(t),
    hasError: isError,
    pledges,
  };
};

type FinancialPledgeListProps = {
  pledges: GraphQLPledge[];
};

const FinancialPledgeList: React.FC<FinancialPledgeListProps> = ({
  pledges,
}) => {
  const { t } = useTranslation(['my']);

  const [activePledgeId, setPledgeIdQuery] = useQueryState('id', {
    history: 'push',
  });
  const setActiveQueryId = (open: boolean, pledgeId: string) => {
    if (open) {
      setPledgeIdQuery(pledgeId);
    } else {
      setPledgeIdQuery(null);
    }
  };

  /**
   * Scroll to active card after page loads.
   */
  useEffect(() => {
    if (!activePledgeId) {
      return;
    }

    const activeCard = document.getElementById(getCardElId(activePledgeId));
    if (!activeCard) {
      return;
    }

    activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return (
    <div key="commitment-group-pledge" className="CommitmentLists__group">
      <SectionCardList heading={t('commitments.frequencies.financialPledges')}>
        {pledges.map((pledge) => {
          if (pledge.pledgeId === null) {
            return;
          }

          const commitmentCardData: CommitmentCardOverrideData = {
            ...(pledge.pledgeName && {
              title: formatPledgeName(pledge.pledgeName, t),
            }),
            label: t('commitments.pledges.subTitle'),
            rate: pledge.totalPledgeAmount,
            footer: formatPledgeFooter(t, pledge.nextInstallmentDate),
            pledge,
          };
          const id = getCardElId(pledge.pledgeId!);

          return (
            <div id={id} key={id}>
              <CommitmentCard
                overrideData={commitmentCardData}
                onActiveChange={setActiveQueryId}
                isActive={pledge.pledgeId === activePledgeId}
              />
            </div>
          );
        })}
      </SectionCardList>
    </div>
  );
};

export type CommitmentListGroupsProps = {
  sortCommitmentsByTitle: (
    a: GraphQLCommitment,
    b: GraphQLCommitment,
  ) => number;
  commitmentFrequencyGroups: CommitmentCardData[];
};

export const CommitmentListGroups: React.FC<CommitmentListGroupsProps> = ({
  sortCommitmentsByTitle,
  commitmentFrequencyGroups,
}) => {
  const { t } = useTranslation('common');
  const [activeCommitmentId, setCommitmentIdQuery] = useQueryState('id', {
    history: 'push',
  });
  const setActiveQueryId = (open: boolean, commitmentId: string) => {
    if (open) {
      setCommitmentIdQuery(commitmentId);
    } else {
      setCommitmentIdQuery(null);
    }
  };

  /**
   * Scroll to active card after page loads.
   */
  useEffect(() => {
    if (!activeCommitmentId) {
      return;
    }

    const activeCard = document.getElementById(getCardElId(activeCommitmentId));
    if (!activeCard) {
      return;
    }

    activeCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  return commitmentFrequencyGroups.map(
    (
      { heading, frequencyAbbr, commitments: commitmentsByFrequency },
      index,
    ) => {
      const commitmentsByType = groupCommitmentsByType(commitmentsByFrequency);

      /** NOTE: Pull childSponsorships and giftCommitments out so that we can list before other commitment types */
      const {
        [CommitmentTypeEnum.ChildSponsorship]: childSponsorships,
        [CommitmentTypeEnum.GiftCommitment]: giftCommitments,
        ...remainingCommitmentTypes
      } = commitmentsByType;
      const remainingCommitments = Object.keys(remainingCommitmentTypes)
        .map((commitmentType) => remainingCommitmentTypes[commitmentType])
        .flat();

      return (
        groupHasCommitments(commitmentsByType) && (
          <div
            className="CommitmentLists__group"
            key={`commitment-group-${heading}`}
          >
            <SectionCardList
              heading={heading}
              subheading={
                <>
                  {t('numbers.currency', {
                    amount: getCommitmentTotal(commitmentsByFrequency),
                  })}
                  <span className="label-neutral-mild">{frequencyAbbr}</span>
                </>
              }
            >
              {[
                ...childSponsorships.toSorted(sortCommitmentsByTitle),
                ...giftCommitments.toSorted(sortCommitmentsByTitle),
                ...remainingCommitments.toSorted(sortCommitmentsByTitle),
              ].map((commitment) => {
                const id = getCardElId(commitment.id);
                return (
                  <div id={id} key={id}>
                    <CommitmentCard
                      commitment={commitment}
                      onActiveChange={setActiveQueryId}
                      isActive={commitment.id === activeCommitmentId}
                    />
                  </div>
                );
              })}
            </SectionCardList>
          </div>
        )
      );
    },
  );
};

/**
 * Displays list of commitments based on frequency.
 * @TODO - need to talk to UX about UI when there are no commitments.
 */
export const CommitmentLists = () => {
  const {
    isShowingSkeletons,
    commitmentFrequencyGroups,
    hasError,
    pledges,
    sortCommitmentsByTitle,
  } = useComponentData();
  const { t } = useTranslation(['my', 'common']);

  return (
    <div className="CommitmentLists max-w-md">
      {isShowingSkeletons && (
        <SectionCardList isLoadingCards isLoadingHeading />
      )}

      {!isShowingSkeletons && !!pledges?.length && (
        <FinancialPledgeList pledges={pledges} />
      )}

      {!isShowingSkeletons && !!commitmentFrequencyGroups.length && (
        <CommitmentListGroups
          sortCommitmentsByTitle={sortCommitmentsByTitle}
          commitmentFrequencyGroups={commitmentFrequencyGroups}
        />
      )}

      {!isShowingSkeletons &&
        !hasError &&
        !commitmentFrequencyGroups.length &&
        !pledges?.length && (
          <>
            <h3 className="heading-sm mb-snug-xl">
              {t('my:commitments.noCommitments.title')}
            </h3>

            <p className="body-sm label-neutral-mild">
              {t('my:commitments.noCommitments.message')}
            </p>

            <p className="flex">
              <ButtonStyle className="" size="md" variant="primary">
                <a
                  href={`${process.env.NEXT_PUBLIC_BASE_URL_DOTCOM}/sponsor-a-child`}
                >
                  {t('commitments.noCommitments.ctaText')}
                </a>
              </ButtonStyle>
            </p>
          </>
        )}

      {!isShowingSkeletons && hasError && (
        <Alert
          variant="negative"
          title={t('commitments.queryError.title')}
          message={t('commitments.queryError.message')}
          action={
            <a href={CTA_CONTACT_SUPPORT.url}>
              {t(CTA_CONTACT_SUPPORT.translation)}
            </a>
          }
        />
      )}
    </div>
  );
};
